/**
 * SortableToolbarItem
 * Transparent, content-sized wrapper that makes one toolbar row sortable with
 * the package's dependency-free sortable (see ./sortable). Renders NO styling
 * of its own: the original visual structure (`.toolbar > div` sizing,
 * `.toolbar-bottom-group` sticky block, GenericToolbar wrappers) stays exactly
 * as it was — this wrapper only forwards the transform/transition and the
 * drag listeners to its child slot.
 *
 * Drag motion mirrors the minimized sidepanel rail: the dragged icon rides
 * the pointer while the other icons slide over to make room.
 */

import React from 'react';
import { useSortable } from './sortable';

interface SortableToolbarItemProps {
    id: string;
    /** When true the row is no longer draggable (layout locked) */
    dragDisabled?: boolean;
    children: React.ReactNode;
}

const SortableToolbarItem: React.FC<SortableToolbarItemProps> = ({
    id,
    dragDisabled = false,
    children,
}) => {
    const {
        setNodeRef,
        listeners,
        transform,
        transition,
        isDragging,
    } = useSortable({ id, disabled: dragDisabled });

    return (
        <div
            ref={setNodeRef}
            {...listeners}
            data-sortable-id={id}
            className={isDragging ? 'toolbar-item-drag-source' : undefined}
            style={{
                transform:
                    transform === null
                        ? undefined
                        : `translate3d(${transform.x}px, ${transform.y}px, 0)`,
                transition,
            }}
        >
            {children}
        </div>
    );
};

export default SortableToolbarItem;
